import { Badge, Button, Flex, Heading, Text } from '@chakra-ui/react';

import { formatDate, isDefined, isNotDefined } from '@enterprise/core';
import {
  ArrowUpRightFromSquare,
  Calculator,
  DefaultToolTip,
  FloppyDisk,
  PercentFormat,
  SquareArrowDownLeft,
  TriangleExclamation,
} from '@enterprise/ui';

import { ROW_HEIGHT } from '@/constants/grid';

import type { NthCalculatorProps } from './types/nth-calculator.props';

import { nthThresholdPercentage } from './common/nth-threshold';
import NthCalculatorGrid from './components/nth-calculator-grid';
import PackageTargetEditor from './components/package-target-editor/package-target-editor';
import { useNthCalculatorConfiguration } from './hooks/nth-calculator-configuration.hook';
import { useNthCalculatorStore } from './stores/nth-calculator-store';

export const NthCalculator = ({ mailingId, onNewWindow, onReturnToPanel }: NthCalculatorProps) => {
  const draftUpdatedAt = useNthCalculatorStore(({ drafts }) => {
    if (isNotDefined(mailingId)) {
      return undefined;
    }

    return drafts[mailingId]?.updatedAt;
  });
  const draftSavedAt = isDefined(draftUpdatedAt) ? formatDate(new Date(draftUpdatedAt), 'h:mm a') : undefined;

  const {
    dropBottom4Ref,
    getMailingListUpdatesRef,
    handleNewWindow,
    handleOverThresholdChange,
    handleUpdate,
    isOverThreshold,
    packageTarget,
    resetRef,
    totalFirstOutput,
  } = useNthCalculatorConfiguration({ mailingId, onNewWindow });

  return (
    <Flex
      direction="column"
      h="full"
      w="full">
      <Flex
        align="center"
        bg="purple.800"
        color="white"
        gap={3}
        h={`${ROW_HEIGHT}px`}
        px={3}>
        <Calculator widthAuto />
        <Heading
          isTruncated
          size="sm">
          Nth Calculator
        </Heading>
        <Flex
          align="center"
          gap={3}
          ml="auto">
          {isDefined(draftSavedAt) && (
            <Text
              color="whiteAlpha.600"
              flexShrink={0}
              fontSize="xs">
              Draft saved {draftSavedAt}
            </Text>
          )}
          <DefaultToolTip label="Auto-sets Nth [1-4] and recalculates New Target for all rows">
            <Button
              colorScheme="purple"
              flexShrink={0}
              onClick={() => {
                dropBottom4Ref.current?.();
              }}
              size="xs">
              Drop Bottom 4
            </Button>
          </DefaultToolTip>
          {isDefined(onNewWindow) && (
            <DefaultToolTip label="Open in new window">
              <Button
                colorScheme="gray"
                flexShrink={0}
                leftIcon={<ArrowUpRightFromSquare />}
                onClick={handleNewWindow}
                size="xs">
                New Window
              </Button>
            </DefaultToolTip>
          )}
          {isDefined(onReturnToPanel) && (
            <DefaultToolTip label="Return to side panel">
              <Button
                colorScheme="purple"
                flexShrink={0}
                leftIcon={<SquareArrowDownLeft />}
                onClick={onReturnToPanel}
                size="xs">
                Return to Panel
              </Button>
            </DefaultToolTip>
          )}
        </Flex>
      </Flex>
      <Flex grow={1}>
        <NthCalculatorGrid
          dropBottom4Ref={dropBottom4Ref}
          getMailingListUpdatesRef={getMailingListUpdatesRef}
          mailingId={mailingId}
          nthThresholdPercentage={nthThresholdPercentage}
          onOverThresholdChange={handleOverThresholdChange}
          packageTarget={packageTarget}
          resetRef={resetRef}
        />
      </Flex>
      <Flex
        align="center"
        bg="gray.100"
        border="1px solid"
        borderColor="gray.300"
        gap={3}
        p={3}>
        <Flex
          align="center"
          gap={2}>
          <Text
            color="gray.500"
            fontSize="sm">
            Package Target:
          </Text>
          <PackageTargetEditor
            mailingId={mailingId}
            packageTarget={packageTarget}
            totalFirstOutput={totalFirstOutput}
          />
          <Text
            color="gray.500"
            fontSize="sm">
            Threshold:
          </Text>
          <Badge
            colorScheme="purple"
            fontSize="xs"
            px={2}
            py={1}
            rounded="md">
            <PercentFormat
              decimals={0}
              value={nthThresholdPercentage / 100}
            />
          </Badge>
        </Flex>
        <Button
          ml="auto"
          onClick={() => {
            resetRef.current?.();
          }}
          size="sm">
          Cancel
        </Button>
        <DefaultToolTip
          isDisabled={!isOverThreshold}
          label={`New target exceeds the threshold of ${nthThresholdPercentage}% over the package target.`}>
          <Button
            colorScheme={isOverThreshold ? 'yellow' : 'green'}
            leftIcon={<FloppyDisk />}
            onClick={handleUpdate}
            rightIcon={isOverThreshold ? <TriangleExclamation widthAuto /> : undefined}
            size="sm">
            Update
          </Button>
        </DefaultToolTip>
      </Flex>
    </Flex>
  );
};
